import { safeAwaitAll, SafeError, SafePromise } from '@benzinga/safe-await';
import { ListenableSubscribable } from '@benzinga/subscribable';
import {
  ArticleSearchResponse,
  BreakingNewsResponse,
  NodeQueryParams,
  ParselyPost,
  ParselyPostParams,
  News,
  StoryObject,
  ChartbeatPost,
  ChartbeatParams,
} from './entities';
import { NodeContentRestful, BasicNewsRestful } from './restful';
import { Session } from '@benzinga/session';
import { SimpleNewsOptions, SimpleNewsQuery } from '@benzinga/internal-news-manager';

interface BasicNewsRequestErrorEvent {
  error: SafeError;
  errorType:
    | 'news:simply_query_news_error'
    | 'news:fetch_nodes_error'
    | 'news:get_breaking_news_error'
    | 'news:get_latest_articles_error'
    | 'news:get_top_stories_error'
    | 'news:get_top_story_ids_error'
    | 'news:get_trending_india_stories_ids_error'
    | 'news:get_premium_articles_error'
    | 'article:get_article_error';
  type: 'error';
}

export type BasicNewsRestfulEvent = BasicNewsRequestErrorEvent;

export type NewsRequestSubscription = ReturnType<BasicNewsRequest['subscribe']>;

export class BasicNewsRequest extends ListenableSubscribable<BasicNewsRestfulEvent> {
  private restful: BasicNewsRestful;
  private restfulNodeContent: NodeContentRestful;

  constructor(session: Session) {
    super();
    this.restful = new BasicNewsRestful(session);
    this.restfulNodeContent = new NodeContentRestful(session);
  }

  public getTrendingNews = async (): SafePromise<News[][]> => {
    const response = await this.restful.getTrendingNews();
    if (response.err) {
      return { err: response.err };
    }
    return response;
  };

  public simplyQueryNews = async (
    query: SimpleNewsQuery = {},
    options: SimpleNewsOptions = {},
  ): SafePromise<StoryObject[]> => {
    const response = await this.restful.simplyQueryNews(query, options);
    if (response.err) {
      this.dispatch({ error: response.err, errorType: 'news:simply_query_news_error', type: 'error' });
      return { err: response.err };
    }
    return response;
  };

  public fetchNodes = async (query: NodeQueryParams = {}): SafePromise<News[]> => {
    const response = await this.restfulNodeContent.fetchNodes(query);
    if (response.err) {
      this.dispatch({ error: response.err, errorType: 'news:fetch_nodes_error', type: 'error' });
      return { err: response.err };
    }
    return response;
  };

  public getBreakingNews = async (): SafePromise<BreakingNewsResponse> => {
    const response = await this.restful.getBreakingNews();
    if (response.err) {
      this.dispatch({ error: response.err, errorType: 'news:get_breaking_news_error', type: 'error' });
      return response;
    }
    return response;
  };

  public getLatestArticles = async (query: string): SafePromise<ArticleSearchResponse> => {
    const response = await this.restful.getLatestArticles(query);
    if (response.err) {
      this.dispatch({ error: response.err, errorType: 'news:get_latest_articles_error', type: 'error' });
      return response;
    }
    return response;
  };

  public getTopStoryIdsFromNodeQueue = async (): SafePromise<number[]> => {
    const response = await this.restful.getTopStoryIdsFromNodeQueue();
    if (response.err) {
      this.dispatch({ error: response.err, errorType: 'news:get_top_story_ids_error', type: 'error' });
      return response;
    }
    return response;
  };

  public getStory = async (id: number): SafePromise<News> => {
    return await this.restfulNodeContent.getStory(id);
  };

  public getStoriesWithIds = async (ids: number[]): SafePromise<News[]> => {
    const reqs = ids.map(id => this.getStory(id));

    const response = await safeAwaitAll(reqs);

    if (response.err && response.err.length) {
      return { err: response.err[0] };
    }

    if (Array.isArray(response.ok)) {
      return {
        ok: response.ok
          .map(node => {
            return node?.[0];
          })
          .filter(node => node),
      };
    } else {
      return { ok: [] };
    }
  };

  getTopPostsUsingChartbeat = async (params: ChartbeatParams = {}): SafePromise<{ pages: ChartbeatPost[] }> => {
    const response = await this.restful.getTopPostsUsingChartbeat(params);
    return response;
  };
}
